import { jsxs, jsx } from "react/jsx-runtime";
import { useState, useRef, useEffect, useCallback, useMemo } from "react";
import { throttle } from "@perxis-auto/shared";
import AntTooltip from "antd/es/tooltip";
import classnames from "../../utils/classnames.js";
import Button from "../Button/Button.js";
import Col from "../Grid/Col.js";
import Row from "../Grid/Row.js";
import './Tooltip.css';import '../Grid/Grid.css';/* empty css                */
import Icon from "../Icon/Icon.js";
/* empty css             */
const renderNode = (node) => {
  return typeof node === "function" ? node() : node;
};
const Tooltip = ({
  className,
  classNames,
  buttonClassName,
  triggerClassName,
  children,
  open = false,
  showCloseButton,
  title,
  content,
  onOpenChange,
  hasTouchListener = false,
  ...props
}) => {
  const [isTooltipOpen, setIsTooltipOpen] = useState(open);
  const [isClosedWithCloseButton, setIsClosedWithCloseButton] = useState(false);
  const wrapRef = useRef(null);
  const handleTouchMove = (e) => {
    if (!wrapRef.current) {
      return;
    }
    if (!wrapRef.current.contains(e.target)) {
      setIsTooltipOpen(false);
    }
  };
  const handleTouchStart = (e) => {
    const tooltip = document.querySelector('[role="tooltip"]');
    const target = e.target;
    if (!(tooltip == null ? void 0 : tooltip.contains(target))) {
      setIsTooltipOpen(false);
    }
  };
  useEffect(() => {
    if (isTooltipOpen) {
      const touchThrottle = throttle(handleTouchMove, 500);
      document.addEventListener("touchmove", touchThrottle);
      document.addEventListener("scroll", touchThrottle);
      if (hasTouchListener) {
        document.addEventListener("touchstart", handleTouchStart);
      }
      return () => {
        document.removeEventListener("touchmove", touchThrottle);
        document.removeEventListener("scroll", touchThrottle);
        if (hasTouchListener) {
          document.removeEventListener("touchstart", handleTouchStart);
        }
      };
    }
  }, [hasTouchListener, isTooltipOpen]);
  useEffect(() => {
    setIsTooltipOpen(open);
  }, [open]);
  const openChange = useCallback(
    (e) => {
      if (isClosedWithCloseButton) {
        setIsClosedWithCloseButton(false);
        return;
      }
      if (onOpenChange) {
        onOpenChange(e);
      }
      setIsTooltipOpen(e);
    },
    [onOpenChange, isClosedWithCloseButton]
  );
  const tooltipClick = useCallback((e) => {
    const target = e.target;
    if (target.closest("label")) {
      return;
    }
    if (target.closest('input[type="checkbox"]')) {
      return;
    }
    const tag = target.tagName.toLowerCase();
    const role = target.getAttribute("role") ?? "";
    const tabIndex = target.getAttribute("tabindex");
    const isDisabled = target.getAttribute("disabled") !== null || target.getAttribute("aria-disabled") === "true";
    const isNativeInteractive = ["a", "button", "input", "label", "textarea", "select"].includes(tag);
    const isSemanticInteractive = ["button", "checkbox", "link", "radio", "textbox"].includes(role);
    const isFocusable = tabIndex !== null && tabIndex !== "-1";
    const isTrulyInteractive = isNativeInteractive || isSemanticInteractive && !isDisabled || isFocusable;
    if (!isTrulyInteractive) {
      e.preventDefault();
    }
  }, []);
  const tooltipContent = useMemo(() => {
    if (!title) {
      return;
    }
    return /* @__PURE__ */ jsxs(Row, { className: classnames("perxis-auto-tooltip-content"), "data-perxis-auto-stop-propagation": true, children: [
      /* @__PURE__ */ jsx(Col, { span: 24, children: /* @__PURE__ */ jsxs(Row, { className: classnames("perxis-auto-tooltip-title"), children: [
        renderNode(title),
        showCloseButton && /* @__PURE__ */ jsx("span", { className: classnames("perxis-auto-tooltip-close-button"), children: /* @__PURE__ */ jsx(
          Button,
          {
            type: "text",
            onClick: () => {
              setIsTooltipOpen((isTooltipOpen2) => !isTooltipOpen2);
              setIsClosedWithCloseButton(true);
            },
            children: /* @__PURE__ */ jsx(Icon, { name: "cross", size: 20 })
          }
        ) })
      ] }) }),
      content && /* @__PURE__ */ jsx(Col, { span: 24, children: renderNode(content) })
    ] });
  }, [title, content, showCloseButton]);
  return /* @__PURE__ */ jsx(
    "div",
    {
      role: "button",
      className: classnames("perxis-auto-tooltip-button", buttonClassName),
      tabIndex: 0,
      ref: wrapRef,
      onKeyDown: tooltipClick,
      onClickCapture: tooltipClick,
      "data-perxis-auto-stop-propagation": true,
      children: /* @__PURE__ */ jsx(
        AntTooltip,
        {
          ...props,
          title: tooltipContent,
          open: isTooltipOpen,
          onOpenChange: openChange,
          classNames: {
            root: classnames("perxis-auto-tooltip", [className, classNames == null ? void 0 : classNames.root]),
            body: classNames == null ? void 0 : classNames.body
          },
          children: /* @__PURE__ */ jsx("div", { className: classnames("perxis-auto-tooltip-trigger", triggerClassName), children })
        }
      )
    }
  );
};
export {
  Tooltip as default
};
//# sourceMappingURL=Tooltip.js.map
